import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { PublicNavbar } from "@/components/layout/PublicNavbar";
import { PublicFooter } from "@/components/layout/PublicFooter";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import {
  Search,
  ShieldCheck,
  FileSpreadsheet,
  Database,
  Users,
  Building2,
  Microscope,
  Terminal,
  Lock,
  Layers,
  AlertTriangle,
  Info,
  CheckCircle2,
  ExternalLink,
} from "lucide-react";

export function DocumentationPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSection, setActiveSection] = useState("overview");

  // Section navigation links
  const sections = [
    { id: "overview", label: "1. Overview & Architecture", icon: Microscope },
    { id: "roles", label: "2. User Roles & Permissions", icon: Users },
    { id: "entry-protocol", label: "3. Bench Entry Protocol", icon: Terminal },
    { id: "appendix-a", label: "4. Appendix A: Antibiotic Key", icon: Database },
    { id: "appendix-b", label: "5. Appendix B: Organism List", icon: Layers },
    { id: "appendix-c", label: "6. Appendix C: Wards & Specimens", icon: Building2 },
    { id: "qa-rules", label: "7. QA Validation Rules", icon: ShieldCheck },
    { id: "clsi-m39", label: "8. CLSI M39 Antibiograms", icon: FileSpreadsheet },
    { id: "whonet-export", label: "9. BacLink & WHONET Export", icon: ExternalLink },
    { id: "security-backup", label: "10. Security & Backups", icon: Lock },
  ];

  // Appendix A Reference Data
  const antibioticsData = [
    { shorthand: "Amp", name: "Ampicillin", code: "AMP", class: "Penicillins", status: "Approved" },
    { shorthand: "Amox", name: "Amoxicillin", code: "AMX", class: "Penicillins", status: "Approved" },
    { shorthand: "Aug, Amox-clav", name: "Amoxicillin-clavulanate (Augmentin)", code: "AMC", class: "Penicillin + inhibitor", status: "Approved" },
    { shorthand: "Pen", name: "Benzylpenicillin (penicillin G)", code: "PEN", class: "Penicillins", status: "Approved" },
    { shorthand: "Clox", name: "Cloxacillin", code: "CLO", class: "Penicillins (anti-staph)", status: "Approved" },
    { shorthand: "Ox", name: "Oxacillin", code: "OXA", class: "Penicillins (anti-staph)", status: "Approved" },
    { shorthand: "Pip-Tazo, Tazo", name: "Piperacillin-tazobactam", code: "TZP", class: "Penicillin + inhibitor", status: "Approved" },
    { shorthand: "Cxm", name: "Cefuroxime", code: "CXM", class: "Cephalosporin (2nd gen)", status: "Approved" },
    { shorthand: "Ceftri, Ceftriax", name: "Ceftriaxone", code: "CRO", class: "Cephalosporin (3rd gen)", status: "Approved" },
    { shorthand: "Ctx, Cefotax", name: "Cefotaxime", code: "CTX", class: "Cephalosporin (3rd gen)", status: "Approved" },
    { shorthand: "Caz, Ceftaz", name: "Ceftazidime", code: "CAZ", class: "Cephalosporin (3rd gen)", status: "Approved" },
    { shorthand: "Cpd", name: "Cefpodoxime", code: "CPD", class: "Cephalosporin (3rd gen)", status: "Approved" },
    { shorthand: "Cfm, Cefix", name: "Cefixime", code: "CFM", class: "Cephalosporin (3rd gen)", status: "Approved" },
    { shorthand: "Fep, Cefep", name: "Cefepime", code: "FEP", class: "Cephalosporin (4th gen)", status: "Approved" },
    { shorthand: "Fox", name: "Cefoxitin", code: "FOX", class: "Cephamycin", status: "Approved" },
    { shorthand: "Mem, Mero", name: "Meropenem", code: "MEM", class: "Carbapenem", status: "Approved" },
    { shorthand: "Ipm, Imi", name: "Imipenem", code: "IPM", class: "Carbapenem", status: "Approved" },
    { shorthand: "Etp", name: "Ertapenem", code: "ETP", class: "Carbapenem", status: "Approved" },
    { shorthand: "Gent, Gen", name: "Gentamicin", code: "GEN", class: "Aminoglycoside", status: "Approved" },
    { shorthand: "Amk, Amik", name: "Amikacin", code: "AMK", class: "Aminoglycoside", status: "Approved" },
    { shorthand: "Cip, Cipro", name: "Ciprofloxacin", code: "CIP", class: "Fluoroquinolone", status: "Approved" },
    { shorthand: "Levo, Lev", name: "Levofloxacin", code: "LVX", class: "Fluoroquinolone", status: "Approved" },
    { shorthand: "Oflo, Ofl", name: "Ofloxacin", code: "OFX", class: "Fluoroquinolone", status: "Approved" },
    { shorthand: "Nal", name: "Nalidixic acid", code: "NAL", class: "Quinolone", status: "Approved" },
    { shorthand: "Nit, Nitro", name: "Nitrofurantoin", code: "NIT", class: "Nitrofuran (urine only)", status: "Approved" },
    { shorthand: "Cot, Sxt, Septrin", name: "Co-trimoxazole (trimethoprim-sulfamethoxazole)", code: "SXT", class: "Folate inhibitor", status: "Approved" },
    { shorthand: "Chl, Chlor", name: "Chloramphenicol", code: "CHL", class: "Phenicol", status: "Approved" },
    { shorthand: "Tet", name: "Tetracycline", code: "TCY", class: "Tetracycline", status: "Approved" },
    { shorthand: "Dox", name: "Doxycycline", code: "DOX", class: "Tetracycline", status: "Approved" },
    { shorthand: "Ery", name: "Erythromycin", code: "ERY", class: "Macrolide", status: "Approved" },
    { shorthand: "Azm, Azith", name: "Azithromycin", code: "AZM", class: "Macrolide", status: "Approved" },
    { shorthand: "Cli, Clinda", name: "Clindamycin", code: "CLI", class: "Lincosamide", status: "Approved" },
    { shorthand: "Van, Vanco", name: "Vancomycin", code: "VAN", class: "Glycopeptide (Gram-positive only)", status: "Approved" },
    { shorthand: "Lzd, Line", name: "Linezolid", code: "LNZ", class: "Oxazolidinone (Gram-positive only)", status: "Approved" },
    // Confirmed and signed-off antibiotics (formerly flagged, now signed off by Consultant Microbiologist)
    { shorthand: "Ampiclox", name: "Ampicillin + cloxacillin combination", code: "APX", class: "Penicillin combination", status: "Approved" },
    { shorthand: "Sulbactam", name: "Ceftriaxone-sulbactam combination", code: "CSU", class: "Cephalosporin + inhibitor", status: "Approved" },
    { shorthand: "Cefo", name: "Cefpodoxime", code: "CFO", class: "Cephalosporin (3rd gen)", status: "Approved" },
    { shorthand: "Room, Rocephin", name: "Rocephin (Ceftriaxone brand)", code: "ROC", class: "Cephalosporin (3rd gen)", status: "Approved" },
  ];

  // Appendix B Organisms
  const organismsData = [
    { name: "Escherichia coli", gram: "Negative", sites: "Urine, blood, wound, stool", note: "Common urinary pathogen" },
    { name: "Klebsiella pneumoniae", gram: "Negative", sites: "Urine, blood, respiratory, wound", note: "Often ESBL producer" },
    { name: "Klebsiella species", gram: "Negative", sites: "Where only genus is stated", note: "Genus-level entry (do not guess species)" },
    { name: "Proteus mirabilis", gram: "Negative", sites: "Urine, wound", note: "Swarming, alkaline urine" },
    { name: "Proteus vulgaris", gram: "Negative", sites: "Wound, urine", note: "Indole-positive" },
    { name: "Proteus species", gram: "Negative", sites: "Where only genus is stated", note: "Genus-level entry" },
    { name: "Pseudomonas aeruginosa", gram: "Negative", sites: "Wound, respiratory, urine, blood", note: "Non-fermenter, intrinsically resistant" },
    { name: "Enterobacter cloacae", gram: "Negative", sites: "Urine, wound, blood", note: "AmpC beta-lactamase producer" },
    { name: "Enterobacter aerogenes", gram: "Negative", sites: "Respiratory, wound, blood", note: "Klebsiella aerogenes" },
    { name: "Enterobacter species", gram: "Negative", sites: "Where only genus is stated", note: "Genus-level entry" },
    { name: "Citrobacter freundii", gram: "Negative", sites: "Urine, wound", note: "Opportunistic Gram-negative" },
    { name: "Citrobacter species", gram: "Negative", sites: "Where only genus is stated", note: "Genus-level entry" },
    { name: "Serratia marcescens", gram: "Negative", sites: "Wound, respiratory, blood", note: "Often pigmented red" },
    { name: "Serratia species", gram: "Negative", sites: "Where only genus is stated", note: "Genus-level entry" },
    { name: "Salmonella species", gram: "Negative", sites: "Blood, stool", note: "Enteric fever / gastroenteritis" },
    { name: "Shigella species", gram: "Negative", sites: "Stool", note: "Bacillary dysentery" },
    { name: "Acinetobacter baumannii", gram: "Negative", sites: "Wound, respiratory, blood, ICU", note: "Hospital-acquired pathogen" },
    { name: "Acinetobacter species", gram: "Negative", sites: "Where only genus is stated", note: "Genus-level entry" },
    { name: "Morganella morganii", gram: "Negative", sites: "Urine, wound", note: "Intrinsically resistant to Colistin" },
    { name: "Haemophilus influenzae", gram: "Negative", sites: "Respiratory, CSF", note: "Fastidious" },
    { name: "Neisseria gonorrhoeae", gram: "Negative", sites: "Genital (urethral, cervical)", note: "Intracellular diplococci" },
    // Gram-positives
    { name: "Staphylococcus aureus", gram: "Positive", sites: "Wound, blood, skin, respiratory", note: "Catalase+, Coagulase+; test Oxacillin/Cefoxitin" },
    { name: "Coagulase-negative Staphylococcus", gram: "Positive", sites: "Blood (often contaminant), catheter", note: "CoNS; verify if repeated bottle" },
    { name: "Streptococcus pneumoniae", gram: "Positive", sites: "Respiratory, blood, CSF", note: "Alpha-hemolytic, optochin sensitive" },
    { name: "Streptococcus pyogenes (Group A)", gram: "Positive", sites: "Throat, wound, skin", note: "Beta-hemolytic, bacitracin sensitive" },
    { name: "Streptococcus agalactiae (Group B)", gram: "Positive", sites: "Genital, blood (neonatal)", note: "Neonatal sepsis" },
    { name: "Enterococcus faecalis", gram: "Positive", sites: "Urine, wound, blood", note: "Intrinsically resistant to Cephalosporins" },
    { name: "Enterococcus faecium", gram: "Positive", sites: "Urine, blood, ICU", note: "Often Ampicillin-resistant, VRE" },
    { name: "Enterococcus species", gram: "Positive", sites: "Where only genus is stated", note: "Genus-level entry" },
    // Yeasts
    { name: "Candida albicans", gram: "Yeast / Fungi", sites: "Urine, HVS, blood, mouth", note: "Leave antibacterial panel blank; flag if unsure" },
    { name: "Candida species (non-albicans)", gram: "Yeast / Fungi", sites: "Urine, blood", note: "Non-albicans yeast" },
  ];

  // Appendix C Wards
  const wardsData = [
    { code: "MMW", name: "Male Medical Ward", dept: "Medicine", type: "Inpatient", notes: "Adult male internal medicine" },
    { code: "FMW", name: "Female Medical Ward", dept: "Medicine", type: "Inpatient", notes: "Adult female internal medicine" },
    { code: "MSW", name: "Male Surgical Ward", dept: "Surgery", type: "Inpatient", notes: "Adult male general/specialist surgery" },
    { code: "FSW", name: "Female Surgical Ward", dept: "Surgery", type: "Inpatient", notes: "Adult female general/specialist surgery" },
    { code: "CHW", name: "Children's Ward / Paediatrics", dept: "Paediatrics", type: "Inpatient", notes: "Pediatric inpatient admissions" },
    { code: "SCBU", name: "Special Care Baby Unit", dept: "Paediatrics", type: "Neonatal ICU", notes: "High vulnerability neonatal isolates" },
    { code: "LW", name: "Labour Ward", dept: "Obstetrics", type: "Inpatient", notes: "Delivery and peripartum care" },
    { code: "PNW", name: "Post-natal Ward", dept: "Obstetrics", type: "Inpatient", notes: "Postpartum maternal admissions" },
    { code: "GYN", name: "Gynaecology Ward", dept: "Gynaecology", type: "Inpatient", notes: "Female reproductive health surgery" },
    { code: "ICU", name: "Intensive Care Unit", dept: "Anaesthesia/ICU", type: "Critical Care", notes: "Severe nosocomial resistance tracking" },
    { code: "A&E", name: "Accident and Emergency", dept: "Emergency", type: "Acute Outpatient", notes: "Acute trauma and medical emergencies" },
    { code: "GOPD", name: "General Outpatient Department", dept: "Outpatients", type: "Outpatient", notes: "Primary hospital outpatient clinics" },
    { code: "AMAC", name: "Amenity / private ward", dept: "Private", type: "Inpatient", notes: "Executive and private inpatient suites" },
    { code: "O&T", name: "Orthopaedics and Trauma", dept: "Surgery", type: "Inpatient", notes: "Fracture and musculoskeletal infections" },
    { code: "ENT", name: "Ear, Nose and Throat", dept: "Surgical", type: "Inpatient/Clinic", notes: "Upper airway and ear infections" },
    { code: "EYE", name: "Ophthalmology", dept: "Surgical", type: "Clinic/Ward", notes: "Ocular swabs and conjunctival specimens" },
    { code: "ENDO", name: "Endocrinology clinic", dept: "Medicine", type: "Outpatient", notes: "Diabetic foot ulcers and endocrine care" },
    { code: "UNK", name: "Ward not recorded on register", dept: "General", type: "Unknown", notes: "Select UNK when register leaves ward blank" },
  ];

  // Filtered lists
  const filteredAntibiotics = useMemo(() => {
    if (!searchQuery) return antibioticsData;
    const q = searchQuery.toLowerCase();
    return antibioticsData.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        a.code.toLowerCase().includes(q) ||
        a.shorthand.toLowerCase().includes(q) ||
        a.class.toLowerCase().includes(q)
    );
  }, [searchQuery, antibioticsData]);

  const filteredOrganisms = useMemo(() => {
    if (!searchQuery) return organismsData;
    const q = searchQuery.toLowerCase();
    return organismsData.filter(
      (o) =>
        o.name.toLowerCase().includes(q) ||
        o.gram.toLowerCase().includes(q) ||
        o.sites.toLowerCase().includes(q)
    );
  }, [searchQuery, organismsData]);

  const filteredWards = useMemo(() => {
    if (!searchQuery) return wardsData;
    const q = searchQuery.toLowerCase();
    return wardsData.filter(
      (w) =>
        w.name.toLowerCase().includes(q) ||
        w.code.toLowerCase().includes(q) ||
        w.dept.toLowerCase().includes(q)
    );
  }, [searchQuery, wardsData]);

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-dvh flex flex-col bg-slate-50 text-slate-900 selection:bg-emerald-100 selection:text-emerald-900">
      <PublicNavbar />

      {/* Docs Header Banner */}
      <div className="border-b border-slate-200 bg-white py-8 px-4 sm:px-6 lg:px-8 shadow-sm">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="rounded bg-emerald-100 text-emerald-800 px-2 py-0.5 text-xs font-semibold">
                Official Manual
              </span>
              <span className="text-xs text-slate-500 font-medium">Version 1.0 &bull; CLSI M39-A4</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              EKSUTH Antibiogram Platform Documentation
            </h1>
            <p className="text-sm text-slate-600">
              Comprehensive guidelines on reference data standards, quality assurance rules, and data entry workflows.
            </p>
          </div>

          {/* Quick Search */}
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              type="search"
              placeholder="Search antibiotics, organisms, wards, rules..."
              className="pl-9 text-sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Main Two-Column Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1 w-full grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sticky Sidebar Navigation */}
        <aside className="lg:col-span-1">
          <div className="sticky top-20 rounded-xl border border-slate-200 bg-white p-3 shadow-sm space-y-1">
            <p className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-slate-500">
              Documentation Index
            </p>
            <nav className="space-y-0.5">
              {sections.map((sec) => {
                const Icon = sec.icon;
                const isActive = activeSection === sec.id;
                return (
                  <button
                    key={sec.id}
                    onClick={() => scrollToSection(sec.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium rounded-lg text-left transition-colors ${
                      isActive
                        ? "bg-emerald-50 text-emerald-900 font-bold border-l-2 border-emerald-600"
                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                    }`}
                  >
                    <Icon className={`h-4 w-4 shrink-0 ${isActive ? "text-emerald-700" : "text-slate-400"}`} />
                    <span className="truncate">{sec.label}</span>
                  </button>
                );
              })}
            </nav>

            <div className="pt-4 border-t border-slate-100 px-3">
              <Link to="/antibiogram">
                <span className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 flex items-center gap-1">
                  View Live Antibiogram Matrix &rarr;
                </span>
              </Link>
            </div>
          </div>
        </aside>

        {/* Content Body */}
        <main className="lg:col-span-3 space-y-16">
          {/* Section 1: Overview & Architecture */}
          <section id="overview" className="scroll-mt-24 space-y-4">
            <div className="border-b border-slate-200 pb-3">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                1. System Overview & Architecture
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                The institutional mission, technological design, and clinical scope of the platform.
              </p>
            </div>

            <div className="prose prose-slate max-w-none text-sm leading-relaxed space-y-4 text-slate-700">
              <p>
                The <strong>EKSUTH Antibiogram Platform</strong> is an antimicrobial resistance surveillance and clinical decision support system
                developed for Ekiti State University Teaching Hospital in Ado-Ekiti, Nigeria. It bridges the gap between paper microbiology daybooks
                and hospital-wide clinical practice.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 not-prose">
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <h4 className="font-bold text-slate-900 text-sm flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Offline-First Client (PWA)
                  </h4>
                  <p className="text-xs text-slate-600 mt-1">
                    Powered by React, TypeScript, and IndexedDB. Data clerks transcribe bench registers with zero latency, even during power cuts
                    or network outages. Saves are queued locally and synchronized automatically when online.
                  </p>
                </div>

                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <h4 className="font-bold text-slate-900 text-sm flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Secure PostgreSQL & FastAPI
                  </h4>
                  <p className="text-xs text-slate-600 mt-1">
                    Asynchronous Python backend utilizing SQLAlchemy 2.0 and PostgreSQL 16. Enforces strict row-level auditing, BCrypt credential
                    hashing, and session rate limiting.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2: User Roles & Permissions */}
          <section id="roles" className="scroll-mt-24 space-y-4">
            <div className="border-b border-slate-200 pb-3">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                2. User Roles & Permission Matrix
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Role separation prevents unilateral modifications and safeguards hospital surveillance data.
              </p>
            </div>

            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-700">
                  <tr>
                    <th className="py-2.5 px-3 font-semibold">Role Name</th>
                    <th className="py-2.5 px-3 font-semibold">User Group</th>
                    <th className="py-2.5 px-3 font-semibold">Core Responsibilities</th>
                    <th className="py-2.5 px-3 font-semibold">Permissions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-600">
                  <tr>
                    <td className="py-2.5 px-3 font-bold text-slate-900">volunteer</td>
                    <td className="py-2.5 px-3">Teams 1 to 5</td>
                    <td className="py-2.5 px-3">Transcription from paper bench registers into the digital platform.</td>
                    <td className="py-2.5 px-3">Create & edit own unverified isolates.</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 px-3 font-bold text-slate-900">team_lead</td>
                    <td className="py-2.5 px-3">Senior Volunteer / MLS</td>
                    <td className="py-2.5 px-3">Supervises volunteer teams, resolves entry discrepancies.</td>
                    <td className="py-2.5 px-3">View team isolates, review QA flag queue.</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 px-3 font-bold text-slate-900">microbiologist</td>
                    <td className="py-2.5 px-3">Consultant Microbiologist</td>
                    <td className="py-2.5 px-3">Clinical authorization, clears QA flags, maintains CLSI breakpoints.</td>
                    <td className="py-2.5 px-3">Full QA resolution, edit reference tables, approve publications.</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 px-3 font-bold text-slate-900">data_manager</td>
                    <td className="py-2.5 px-3">Hospital Data Lead</td>
                    <td className="py-2.5 px-3">Bulk user onboarding, WHONET/BacLink exports, proposes publications.</td>
                    <td className="py-2.5 px-3">Propose publications, BacLink export, bulk upload users.</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 px-3 font-bold text-slate-900">administrator</td>
                    <td className="py-2.5 px-3">Hospital ICT / Systems</td>
                    <td className="py-2.5 px-3">System security, server monitoring, audit log review, database backup.</td>
                    <td className="py-2.5 px-3">Full access including system health and audit logs.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3.5 text-xs text-emerald-900 flex items-start gap-2">
              <Info className="h-4 w-4 shrink-0 text-emerald-700 mt-0.5" />
              <div>
                <strong>Dual Authorization Rule:</strong> An antibiogram publication must be proposed by a Data Manager and
                approved by an independent Consultant Microbiologist. The platform strictly forbids self-approval of publications.
              </div>
            </div>
          </section>

          {/* Section 3: Bench Entry Protocol */}
          <section id="entry-protocol" className="scroll-mt-24 space-y-4">
            <div className="border-b border-slate-200 pb-3">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                3. Bench Entry Protocol & Data Capture
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Optimized keystrokes and standards for transcribing microbiology registers.
              </p>
            </div>

            <div className="space-y-4 text-sm text-slate-700">
              <p>
                To maintain high throughput on busy laboratory benches, the data entry screen supports complete keyboard operation:
              </p>

              <div className="grid sm:grid-cols-3 gap-3 not-prose">
                <div className="rounded border border-slate-200 bg-white p-3 space-y-1">
                  <div className="font-mono text-xs font-bold text-slate-900">N</div>
                  <div className="text-xs text-slate-600">Start a new isolate from anywhere on the workbench.</div>
                </div>
                <div className="rounded border border-slate-200 bg-white p-3 space-y-1">
                  <div className="font-mono text-xs font-bold text-slate-900">Tab / Enter</div>
                  <div className="text-xs text-slate-600">Move quickly between form fields and antibiotic grid cells.</div>
                </div>
                <div className="rounded border border-slate-200 bg-white p-3 space-y-1">
                  <div className="font-mono text-xs font-bold text-slate-900">S / I / R</div>
                  <div className="text-xs text-slate-600">Single keystroke to mark Susceptible, Intermediate, or Resistant.</div>
                </div>
              </div>

              <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-3.5 text-xs text-amber-900 space-y-1.5">
                <div className="font-bold flex items-center gap-1.5 text-amber-950">
                  <AlertTriangle className="h-4 w-4 text-amber-700" />
                  Handling "No Growth" vs Positive Culture:
                </div>
                <p>
                  When a culture yields no pathogen after 48 hours of incubation, toggle the <strong>No growth</strong> switch
                  on the entry form. This records the specimen as sterile and automatically collapses the antibiotic panel,
                  ensuring accurate culture-positivity rate calculations across hospital wards.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4: Appendix A - Antibiotics Key */}
          <section id="appendix-a" className="scroll-mt-24 space-y-4">
            <div className="border-b border-slate-200 pb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                  4. Appendix A: Antibiotic Abbreviation Key
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  Expand register shorthand into approved generic names and WHONET codes.
                </p>
              </div>
              <Badge tone="brand">{filteredAntibiotics.length} Antibiotics Listed</Badge>
            </div>

            <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-3.5 text-xs text-emerald-950 space-y-1">
              <div className="font-bold flex items-center gap-1 text-emerald-900">
                <CheckCircle2 className="h-4 w-4 text-emerald-700" /> Consultant Microbiologist Sign-off Complete
              </div>
              <p>
                All 4 previously flagged entries have been officially confirmed: <strong>Sulbactam</strong> is coded as <strong>Ceftriaxone-sulbactam</strong> (CSU), <strong>Ampiclox</strong> as <strong>Ampicillin-cloxacillin</strong> (APX), <strong>Cefo</strong> as <strong>Cefpodoxime</strong> (CFO), and <strong>Rocephin</strong> as <strong>Ceftriaxone</strong> (ROC). All are approved for standard entry.
              </p>
            </div>

            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm max-h-[500px] overflow-y-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-700 sticky top-0 z-10">
                  <tr>
                    <th className="py-2.5 px-3 font-semibold">Register Shorthand</th>
                    <th className="py-2.5 px-3 font-semibold">Approved Full Name</th>
                    <th className="py-2.5 px-3 font-semibold">WHONET Code</th>
                    <th className="py-2.5 px-3 font-semibold">Class</th>
                    <th className="py-2.5 px-3 font-semibold">Status / Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-600">
                  {filteredAntibiotics.map((ab, idx) => (
                    <tr key={idx} className={ab.status === "FLAG_TBC" ? "bg-red-50/50" : "hover:bg-slate-50/80"}>
                      <td className="py-2 px-3 font-mono font-bold text-slate-900">{ab.shorthand}</td>
                      <td className="py-2 px-3 font-medium text-slate-800">{ab.name}</td>
                      <td className="py-2 px-3 font-mono text-emerald-800 font-semibold">{ab.code}</td>
                      <td className="py-2 px-3 text-slate-600">{ab.class}</td>
                      <td className="py-2 px-3">
                        <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800">
                          Approved
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 5: Appendix B - Organism List */}
          <section id="appendix-b" className="scroll-mt-24 space-y-4">
            <div className="border-b border-slate-200 pb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                  5. Appendix B: Standardized Organism List
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  Gram-negative, Gram-positive, and fungal organisms commonly isolated at EKSUTH.
                </p>
              </div>
              <Badge tone="brand">{filteredOrganisms.length} Organisms Listed</Badge>
            </div>

            <div className="rounded-lg border border-blue-200 bg-blue-50/80 p-3.5 text-xs text-blue-900 space-y-1">
              <div className="font-bold flex items-center gap-1 text-blue-950">
                <Info className="h-4 w-4 text-blue-700" /> Genus-Level Rule:
              </div>
              <p>
                If the register names only the genus (for example <em>"Klebsiella species"</em> or <em>"Proteus sp."</em>),
                choose the genus-level entry. <strong>Do NOT guess or add a species the register did not state.</strong>
              </p>
            </div>

            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm max-h-[500px] overflow-y-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-700 sticky top-0 z-10">
                  <tr>
                    <th className="py-2.5 px-3 font-semibold">Organism Name</th>
                    <th className="py-2.5 px-3 font-semibold">Gram Stain / Type</th>
                    <th className="py-2.5 px-3 font-semibold">Common Specimen Sites</th>
                    <th className="py-2.5 px-3 font-semibold">Clinical Surveillance Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-600">
                  {filteredOrganisms.map((org, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/80">
                      <td className="py-2 px-3 font-semibold text-slate-900 italic">{org.name}</td>
                      <td className="py-2 px-3">
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                            org.gram === "Negative"
                              ? "bg-rose-100 text-rose-800"
                              : org.gram === "Positive"
                              ? "bg-purple-100 text-purple-800"
                              : "bg-amber-100 text-amber-800"
                          }`}
                        >
                          {org.gram}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-slate-600">{org.sites}</td>
                      <td className="py-2 px-3 text-slate-500">{org.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 6: Appendix C - Wards & Specimens */}
          <section id="appendix-c" className="scroll-mt-24 space-y-4">
            <div className="border-b border-slate-200 pb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                  6. Appendix C: Wards & Specimen Types
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  18 standard nursing-approved hospital locations and clinical specimen definitions.
                </p>
              </div>
              <Badge tone="brand">18 Hospital Wards</Badge>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-3.5 text-xs text-amber-950 space-y-1">
              <div className="font-bold flex items-center gap-1 text-amber-900">
                <AlertTriangle className="h-4 w-4 text-amber-700" /> When the ward is blank:
              </div>
              <p>
                If the register does not state a ward, select <strong>UNK (Unknown)</strong> rather than leaving it empty
                or guessing. If a ward name is written but not in this approved list, flag the record so it can be verified.
              </p>
            </div>

            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-700">
                  <tr>
                    <th className="py-2.5 px-3 font-semibold">Ward Code</th>
                    <th className="py-2.5 px-3 font-semibold">Ward / Location Name</th>
                    <th className="py-2.5 px-3 font-semibold">Department</th>
                    <th className="py-2.5 px-3 font-semibold">Care Type</th>
                    <th className="py-2.5 px-3 font-semibold">Clinical Context</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-600">
                  {filteredWards.map((w, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/80">
                      <td className="py-2 px-3 font-mono font-bold text-emerald-800">{w.code}</td>
                      <td className="py-2 px-3 font-semibold text-slate-900">{w.name}</td>
                      <td className="py-2 px-3 text-slate-700">{w.dept}</td>
                      <td className="py-2 px-3">
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-700 font-medium">
                          {w.type}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-slate-500">{w.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 7: QA Validation Rules */}
          <section id="qa-rules" className="scroll-mt-24 space-y-4">
            <div className="border-b border-slate-200 pb-3">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                7. Quality Assurance (QA) Clinical Rules
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Automated clinical engine validating biological plausibility and CLSI intrinsic resistance.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 text-xs">
              <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-mono font-bold text-red-700">Q-01 Intrinsic Resistance</span>
                  <Badge tone="blocking">Blocking</Badge>
                </div>
                <p className="text-slate-600">
                  Blocks saving when an isolate is marked sensitive to a drug to which it has inherent physiological resistance
                  (e.g., <em>Klebsiella</em> with Ampicillin; <em>Proteus</em> with Nitrofurantoin or Colistin).
                </p>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-mono font-bold text-red-700">Q-02 Impossible Phenotypes</span>
                  <Badge tone="blocking">Blocking</Badge>
                </div>
                <p className="text-slate-600">
                  Flags biological contradictions, such as an isolate reported sensitive to a 1st generation cephalosporin
                  while simultaneously resistant to 3rd generation cephalosporins or carbapenems.
                </p>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-mono font-bold text-amber-700">Q-03 Contamination Warning</span>
                  <Badge tone="warning">Warning</Badge>
                </div>
                <p className="text-slate-600">
                  Alerts when skin commensals (e.g. Coagulase-negative Staphylococci, <em>Bacillus species</em>) appear in a single
                  blood culture bottle without clinical sepsis correlation.
                </p>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-mono font-bold text-amber-700">Q-04 Unconfirmed Drug Shorthand</span>
                  <Badge tone="warning">Warning</Badge>
                </div>
                <p className="text-slate-600">
                  Forces flag review whenever a volunteer inputs unconfirmed register names (Ampiclox, Sulbactam, Cefo, Rocephin)
                  preventing misattribution until confirmed by the microbiologist.
                </p>
              </div>
            </div>
          </section>

          {/* Section 8: CLSI M39 Cumulative Antibiograms */}
          <section id="clsi-m39" className="scroll-mt-24 space-y-4">
            <div className="border-b border-slate-200 pb-3">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                8. Cumulative Antibiogram Guidelines (CLSI M39-A4)
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Standard statistical criteria for producing reliable institutional susceptibility reports.
              </p>
            </div>

            <div className="space-y-4 text-sm text-slate-700 leading-relaxed">
              <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
                <h4 className="font-bold text-slate-900 text-sm">Key CLSI M39 Principles Enforced by the Platform:</h4>
                <ol className="list-decimal pl-5 space-y-2 text-xs text-slate-600">
                  <li>
                    <strong>First-Isolate De-duplication:</strong> Only the first isolate of a given species isolated from an individual patient
                    within a 365-day reporting period is included, regardless of body site or antimicrobial susceptibility profile. This prevents
                    repeat cultures from long-stay ICU patients from skewing resistance percentages.
                  </li>
                  <li>
                    <strong>Statistical 30-Isolate Threshold:</strong> Antibiogram percentages are only statistically reliable when calculated
                    from at least 30 diagnostic isolates (N &ge; 30). For organism-antibiotic pairs with fewer than 30 isolates, percentage
                    rates are withheld or bracketed to prevent clinical distortion.
                  </li>
                  <li>
                    <strong>Diagnostic Isolates Only:</strong> Surveillance isolates, environmental swabs, and routine infection control
                    screening samples are strictly excluded from the clinical antibiogram matrix.
                  </li>
                </ol>
              </div>
            </div>
          </section>

          {/* Section 9: BacLink & WHONET National Export */}
          <section id="whonet-export" className="scroll-mt-24 space-y-4">
            <div className="border-b border-slate-200 pb-3">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                9. BacLink & WHONET National Export
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Standardized data interchange for WHO GLASS and the Nigeria Centre for Disease Control (NCDC).
              </p>
            </div>

            <p className="text-sm text-slate-700">
              The platform features an automated BacLink export service. Authorized Data Managers can export verified isolates
              formatted according to the WHO WHONET data dictionary:
            </p>

            <div className="rounded-lg bg-slate-900 text-emerald-400 p-4 font-mono text-xs overflow-x-auto space-y-1">
              <div># EKSUTH AMR Surveillance Export (WHONET BacLink Format)</div>
              <div>IDENTIFICATION,SPEC_DATE,SPEC_TYPE,WARD,ORG_CODE,AMP_ND10,CRO_ND30,MEM_ND10...</div>
              <div>EK-2026-0042,2026-09-15,ur,MMW,eco,R,S,S...</div>
              <div>EK-2026-0043,2026-09-16,bl,SCBU,sau,S,S,S...</div>
            </div>
          </section>

          {/* Section 10: Security & Backups */}
          <section id="security-backup" className="scroll-mt-24 space-y-4">
            <div className="border-b border-slate-200 pb-3">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                10. System Security, Privacy & Backup Policy
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Hospital intranet deployment, patient confidentiality, and automated encrypted backups.
              </p>
            </div>

            <div className="space-y-3 text-xs text-slate-700 leading-relaxed">
              <p>
                The platform is designed to operate securely on the hospital intranet:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-slate-600">
                <li><strong>No Public Internet PHI Exposure:</strong> Publicly visible antibiogram dashboards serve purely anonymized, aggregate percentage statistics. Patient identifying details (hospital number, lab number, name) remain isolated behind staff authentication.</li>
                <li><strong>Nightly Encrypted Backups:</strong> The automated backup utility (<code>ops/backup.sh</code>) executes daily pg_dump procedures, encrypts archive archives using OpenSSL AES-256-CBC, and rotates files with a 30-day retention policy.</li>
                <li><strong>Tamper-Evident Audit Trail:</strong> Every record mutation, QA clearance, user login, and publication action logs user ID, timestamp, and previous values in the PostgreSQL <code>audit_events</code> ledger.</li>
              </ul>
            </div>
          </section>
        </main>
      </div>

      <PublicFooter />
    </div>
  );
}
